import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import config from '../../../config.js';
import { RefineUiSpecRequest, UiSpecResult } from './ui-spec.models';
import { pollExistingJob, pollJobUntilSettled } from '../../shared/jobs/poll-job';
import { JobStatusResponse, SubmitJobResponse } from '../../shared/jobs/job.models';

/** A refined spec, paired with the id of the new job that produced it. */
export interface RefinedUiSpec {
  jobId: string;
  result: UiSpecResult;
}

/**
 * UiSpecService — polls the tool-api UI specification job that is
 * auto-triggered when document intake analysis finishes, and submits
 * reviewer refinements against it.
 *
 * Unlike the Solution Blueprint, nothing is downloaded here: a spec is small
 * JSON that the wireframe renderer draws directly, so it rides back inside the
 * job result with no S3 detour.
 */
@Injectable({
  providedIn: 'root',
})
export class UiSpecService {
  private apiUrl = `${config.API_GATEWAY_ENDPOINT}/ui-specs`;

  constructor(private http: HttpClient) {}

  /**
   * Polls `GET /ui-specs/{jobId}` until the auto-triggered UI spec job
   * finishes. Generation runs server-side via the AI gateway and typically
   * takes 20-60 seconds — by the time the user reaches the UI Feedback page
   * the job is usually already done, in which case this resolves on the first
   * poll. Callers should show a persistent progress state while it's in flight.
   */
  pollUiSpec(jobId: string): Observable<UiSpecResult> {
    return pollExistingJob<UiSpecResult>(jobId, (id) => this.getUiSpecStatus(id));
  }

  /**
   * Submits a reviewer's refinement and polls the resulting job.
   *
   * The backend creates a NEW job rather than mutating `jobId`, so the version
   * the reviewer is looking at survives — that is what makes the page's
   * before/after comparison honest. The new job's id comes back alongside the
   * refined spec, and becomes the base for any further refinement; callers keep
   * the previous spec themselves to show next to it.
   */
  refine(jobId: string, feedback: string): Observable<RefinedUiSpec> {
    const body: RefineUiSpecRequest = { feedback };
    return pollJobUntilSettled(
      this.http.post<SubmitJobResponse>(`${this.apiUrl}/${jobId}/refine`, body),
      (id) => this.getUiSpecStatus(id),
    ).pipe(
      map(({ jobId: refinedJobId, envelope }) => ({
        jobId: refinedJobId,
        result: envelope.result as unknown as UiSpecResult,
      })),
    );
  }

  /** GETs the current status of a previously submitted UI spec job. */
  private getUiSpecStatus(jobId: string): Observable<JobStatusResponse> {
    return this.http.get<JobStatusResponse>(`${this.apiUrl}/${jobId}`);
  }
}
